"use client";

import { useEffect, useState } from "react";
import { useClientLocale, useSetLocale } from "@/i18n/use-client-locale";
import { Dropdown } from "@heroui/react";
import { Translate, Check, X } from "@/components/icons";
import { localeNames, localeFlags, locales, type Locale } from "@/i18n/config";

const DISMISS_KEY = "encrata_lang_suggest_dismissed";

type Suggestable = Exclude<Locale, "en">;

// Language name (English) + native banner copy for each suggestable locale.
// The country name is injected (localised) into the native message.
type Suggestion = {
  languageEn: string; // English language name ("Spanish")
  messageNative: (country: string) => string;
  dismissNative: string;
  changeNative: string;
};

const SUGGESTIONS: Record<Suggestable, Suggestion> = {
  es: {
    languageEn: "Spanish",
    messageNative: (c) =>
      `Hemos detectado tu ubicación en ${c}. ¿Quieres cambiar al español?`,
    dismissNative: "Descartar",
    changeNative: "Cambiar al español",
  },
  de: {
    languageEn: "German",
    messageNative: (c) =>
      `Wir haben Ihren Standort als ${c} erkannt. Möchten Sie zu Deutsch wechseln?`,
    dismissNative: "Schließen",
    changeNative: "Zu Deutsch wechseln",
  },
  fr: {
    languageEn: "French",
    messageNative: (c) =>
      `Nous avons détecté que vous vous trouvez en ${c}. Souhaitez-vous passer au français ?`,
    dismissNative: "Ignorer",
    changeNative: "Passer au français",
  },
  zh: {
    languageEn: "Chinese",
    messageNative: (c) => `我们检测到您所在的位置是${c}。您想切换到中文吗？`,
    dismissNative: "忽略",
    changeNative: "切换到中文",
  },
  ja: {
    languageEn: "Japanese",
    messageNative: (c) =>
      `あなたの所在地が${c}であることを検出しました。日本語に切り替えますか？`,
    dismissNative: "閉じる",
    changeNative: "日本語に切り替える",
  },
};

// ISO 3166-1 alpha-2 country → suggested locale. Multilingual countries map to
// their most widely-used supported language for this purpose.
const COUNTRY_TO_LOCALE: Record<string, Suggestable> = {
  // Spanish
  ES: "es", MX: "es", AR: "es", CO: "es", CL: "es", PE: "es", VE: "es",
  EC: "es", GT: "es", CU: "es", BO: "es", DO: "es", HN: "es", PY: "es",
  SV: "es", NI: "es", CR: "es", PA: "es", UY: "es", GQ: "es", PR: "es",
  // German
  DE: "de", AT: "de", LI: "de", CH: "de",
  // French
  FR: "fr", MC: "fr", LU: "fr", BE: "fr", SN: "fr", CI: "fr", ML: "fr",
  CM: "fr", CD: "fr", MG: "fr", BF: "fr",
  // Chinese
  CN: "zh", TW: "zh", HK: "zh", MO: "zh", SG: "zh",
  // Japanese
  JP: "ja",
};

const FALLBACK_CODE: Record<Suggestable, string> = {
  es: "ES", de: "DE", fr: "FR", zh: "CN", ja: "JP",
};

function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.match(
    new RegExp("(?:^|; )" + name + "=([^;]*)"),
  );
  return match ? decodeURIComponent(match[1]) : undefined;
}

function countryName(code: string, locale: string): string {
  try {
    const name = new Intl.DisplayNames([locale], { type: "region" }).of(code);
    if (name) return name;
  } catch {
    // ignore
  }
  return code;
}

type Detection = { target: Suggestable; countryCode: string | null };

function fromCountry(code: string | undefined, current: Locale): Detection | null {
  const c = code?.toUpperCase();
  if (c && c in COUNTRY_TO_LOCALE) {
    const target = COUNTRY_TO_LOCALE[c];
    if (target !== current) return { target, countryCode: c };
  }
  return null;
}

// Browser-language fallback when no IP country is available.
function fromBrowser(current: Locale): Detection | null {
  if (typeof navigator === "undefined") return null;
  const prefs = navigator.languages?.length
    ? navigator.languages
    : [navigator.language];
  for (const pref of prefs) {
    const base = pref.split("-")[0].toLowerCase();
    if (base === "en") break; // English preferred → nothing to suggest
    if (base in SUGGESTIONS && base !== current) {
      return { target: base as Suggestable, countryCode: null };
    }
  }
  return null;
}

export function LanguageBanner() {
  const current = useClientLocale();
  const setLocale = useSetLocale();
  const [detection, setDetection] = useState<Detection | null>(null);

  useEffect(() => {
    let dismissed = false;
    try {
      dismissed = Boolean(window.localStorage.getItem(DISMISS_KEY));
    } catch {
      // ignore
    }
    if (dismissed) return;

    let cancelled = false;

    // 1) Country already known from a prior geo lookup (cookie).
    const fromCookie = fromCountry(readCookie("geo_country"), current);
    if (fromCookie) {
      setDetection(fromCookie);
      return;
    }

    // 2) Resolve the visitor's country from their IP (server route), then fall
    //    back to the browser's preferred languages.
    (async () => {
      let result: Detection | null = null;
      try {
        const res = await fetch("/geo", { cache: "no-store" });
        if (res.ok) {
          const data = (await res.json()) as { country?: string | null };
          result = fromCountry(data.country ?? undefined, current);
        }
      } catch {
        // ignore - fall back below
      }
      if (!result) result = fromBrowser(current);
      if (!cancelled && result) setDetection(result);
    })();

    return () => {
      cancelled = true;
    };
  }, [current]);

  if (!detection) return null;

  const { target, countryCode } = detection;
  const s = SUGGESTIONS[target];
  const code = countryCode ?? FALLBACK_CODE[target];
  const countryEn = countryName(code, "en");
  const countryLocal = countryName(code, target);

  const remember = () => {
    try {
      window.localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      // ignore
    }
  };

  const dismiss = () => {
    remember();
    setDetection(null);
  };

  const change = () => changeTo(target);

  const changeTo = (loc: Locale) => {
    remember();
    setLocale(loc);
    setDetection(null);
  };

  return (
    <div className="fixed right-4 top-20 z-50 w-[calc(100%-2rem)] max-w-sm sm:w-sm">
      <div className="rounded-2xl border border-border-secondary bg-white dark:bg-surface-raised p-5 shadow-xl">
        <div className="flex items-start gap-3">
          <div className="min-w-0">
            <p className="text-sm leading-relaxed text-text-primary">
              We have detected your location as {countryEn}. Would you like to
              change to {s.languageEn}?
            </p>
            <p className="mt-1.5 text-sm leading-relaxed text-text-tertiary">
              {s.messageNative(countryLocal)}
            </p>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2.5">
          <button
            type="button"
            onClick={change}
            disabled={false}
            className="inline-flex h-9 flex-1 items-center justify-center gap-1.5 rounded-xl border border-border-secondary bg-transparent px-4 text-sm font-medium text-text-secondary transition-colors hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-50"
          >
            <Check className="size-3.5" />
            {s.changeNative}
          </button>

          <Dropdown>
            <Dropdown.Trigger
              aria-label="Select language"
              className="inline-flex size-9 shrink-0 items-center justify-center rounded-xl border border-border-secondary bg-transparent text-text-secondary transition-colors hover:bg-black/5 dark:hover:bg-white/5"
            >
              <Translate className="size-4" />
            </Dropdown.Trigger>
            <Dropdown.Popover className="heroui min-w-44" placement="bottom end">
              <Dropdown.Menu onAction={(key) => changeTo(key as Locale)}>
                {locales.map((l) => (
                  <Dropdown.Item key={l} id={l} textValue={localeNames[l]}>
                    <span className="flex items-center gap-2">
                      <span className="text-base leading-none">{localeFlags[l]}</span>
                      {localeNames[l]}
                    </span>
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown.Popover>
          </Dropdown>
        </div>

        <button
          type="button"
          onClick={dismiss}
          className="mt-2.5 inline-flex h-9 w-full items-center justify-center gap-1.5 rounded-xl bg-brand text-sm font-semibold text-brand-foreground transition-colors hover:bg-brand-hover"
        >
          <X className="size-3.5" />
          Dismiss
        </button>
      </div>
    </div>
  );
}
